import type { SavedPlace, Trip } from '@itin/shared'
import { zoneCityLabel } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { useLocale, useT } from '../lib/i18n.js'
import {
  destinationsOf,
  fold,
  narrowSavedPlaces,
  SAVED_PLACE_KINDS,
  SavedPlaceFilters,
} from './SavedPlacePicker.js'
import { Button, Card, ErrorNotice, Field, Input, Select } from './ui.js'

/**
 * Ana's own places — the ones she recommends every time.
 *
 * Shown on the trip page rather than on a separate screen, because the moment
 * she wants her Paris list is the moment she is building a Paris trip. A library
 * behind its own navigation item is a library nobody opens.
 *
 * The list leads with the places whose destination matches where this trip is
 * going. Matching is loose and its failure is cheap: a miss costs ordering, not
 * access, so "Paris" and "paris" and "Paris, França" all land in the right half
 * and anything unrecognised is still one search away.
 */

export function SavedPlacesCard({ trip }: { trip: Trip }) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [query, setQuery] = useState('')
  const [destination, setDestination] = useState('')
  const [kind, setKind] = useState('')

  const saved = useQuery({ queryKey: ['savedPlaces'], queryFn: api.listSavedPlaces })

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['savedPlaces'] }),
      queryClient.invalidateQueries({ queryKey: ['plan', trip.id] }),
    ])

  const addToTrip = useMutation({
    mutationFn: (savedPlaceId: string) => api.addSavedPlaceToTrip(trip.id, { savedPlaceId }),
    onSuccess: invalidate,
  })

  const create = useMutation({
    mutationFn: api.createSavedPlace,
    async onSuccess() {
      await invalidate()
      setAdding(false)
    },
  })

  const remove = useMutation({ mutationFn: api.deleteSavedPlace, onSuccess: invalidate })

  const tripCity = zoneCityLabel(trip.timezone, locale)

  // The same function the in-day picker uses, so a place findable in one is
  // findable in the other by the same words.
  const shown = useMemo(
    () => narrowSavedPlaces(saved.data ?? [], { query, destination, kind, here: tripCity }),
    [saved.data, query, destination, kind, tripCity],
  )
  const destinations = useMemo(() => destinationsOf(saved.data ?? []), [saved.data])

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const optional = (key: string) => {
      const value = String(form.get(key) ?? '').trim()
      return value ? { [key]: value } : {}
    }
    create.mutate({
      destination: String(form.get('destination')),
      kind: String(form.get('kind')) as 'restaurant',
      name: String(form.get('name')),
      ...optional('address'),
      ...optional('addressLocal'),
      ...optional('website'),
      ...optional('phone'),
      ...optional('notes'),
    })
  }

  return (
    <Card
      testId="saved-places-card"
      title={t('savedPlaces.title')}
      collapsible
      actions={
        <Button variant="quiet" onClick={() => setAdding((v) => !v)}>
          {t('savedPlaces.add')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-slate-500">{t('savedPlaces.hint')}</p>

        {saved.isError && <ErrorNotice error={saved.error} />}
        {saved.isPending && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {saved.data?.length === 0 && !adding && (
          <p className="text-sm text-slate-500">{t('savedPlaces.empty')}</p>
        )}

        {(saved.data?.length ?? 0) > 0 && (
          <SavedPlaceFilters
            query={query}
            destination={destination}
            kind={kind}
            destinations={destinations}
            onQuery={setQuery}
            onDestination={setDestination}
            onKind={setKind}
          />
        )}

        {(saved.data?.length ?? 0) > 0 && shown.length === 0 && (
          <p className="text-sm text-slate-500">{t('savedPlaces.noMatches')}</p>
        )}

        {addToTrip.isError && <ErrorNotice error={addToTrip.error} />}

        <ul className="flex flex-col divide-y divide-slate-200">
          {shown.map((entry) => (
            <SavedPlaceRow
              key={entry.id}
              entry={entry}
              here={fold(entry.destination).includes(fold(tripCity))}
              busy={addToTrip.isPending}
              onAdd={() => addToTrip.mutate(entry.id)}
              onRemove={() => {
                if (confirm(t('savedPlaces.removeConfirm', { name: entry.name }))) {
                  remove.mutate(entry.id)
                }
              }}
            />
          ))}
        </ul>

        {adding && (
          <form
            onSubmit={submit}
            className="flex flex-col gap-3 rounded-md bg-slate-50 p-3"
          >
            <Field
              label={t('savedPlaces.field.destination')}
              guidance="savedPlaces.field.destination.guidance"
            >
              <Input name="destination" required defaultValue={tripCity} maxLength={120} />
            </Field>
            <Field label={t('savedPlaces.field.kind')} guidance="savedPlaces.field.kind.guidance">
              <Select name="kind" defaultValue="restaurant">
                {SAVED_PLACE_KINDS.map((value) => (
                  <option key={value} value={value}>
                    {t(`savedPlaces.kind.${value}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('places.field.name')} guidance="places.field.name.guidance">
              <Input name="name" required autoFocus maxLength={200} />
            </Field>
            <Field label={t('places.field.address')}>
              <Input name="address" />
            </Field>
            <Field
              label={t('places.field.addressLocal')}
              hint={t('places.field.addressLocalHint')}
            >
              <Input name="addressLocal" />
            </Field>
            <Field label={t('places.field.phone')}>
              <Input name="phone" placeholder="+33144186110" />
            </Field>
            <Field label={t('places.field.website')}>
              <Input name="website" type="url" />
            </Field>
            <Field label={t('places.field.notes')} guidance="savedPlaces.field.notes.guidance">
              <Input name="notes" maxLength={500} />
            </Field>
            {create.isError && <ErrorNotice error={create.error} />}
            <div className="flex gap-2">
              <Button type="submit" disabled={create.isPending}>
                {t('common.save')}
              </Button>
              <Button type="button" variant="quiet" onClick={() => setAdding(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  )
}

function SavedPlaceRow({
  entry,
  here,
  busy,
  onAdd,
  onRemove,
}: {
  entry: SavedPlace
  here: boolean
  busy: boolean
  onAdd(): void
  onRemove(): void
}) {
  const t = useT()
  return (
    <li className="flex flex-wrap items-start justify-between gap-2 py-2">
      <div className="flex min-w-0 flex-col">
        <span className="flex items-center gap-2 font-medium">
          {entry.name}
          {here && (
            <span className="rounded bg-accent/20 px-1.5 py-0.5 text-xs font-medium text-slate-700">
              {t('savedPlaces.here')}
            </span>
          )}
        </span>
        <span className="text-xs text-slate-500">
          {t(`savedPlaces.kind.${entry.kind}`)} · {entry.destination}
          {entry.address ? ` · ${entry.address}` : ''}
        </span>
        {entry.notes && (
          <span className="text-xs text-slate-600">{entry.notes}</span>
        )}
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="quiet" disabled={busy} onClick={onAdd}>
          {t('savedPlaces.addToTrip')}
        </Button>
        <Button variant="danger" onClick={onRemove}>
          {t('common.remove')}
        </Button>
      </div>
    </li>
  )
}
